import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import {
  RequireAuth,
  RequireGuest,
  RequirePermission,
  RequireRole,
} from '@/components/navigation/RouteGuards';
import { AppLoadingState } from '@/components/feedback/AppLoadingState';
import { ROLES, STAFF_ROLES } from '@/config/permissions';
import { useCurrentUser } from '@/features/auth/hooks/useAuth';

// Section 54: route-level code splitting - each page is its own chunk,
// only downloaded when a person actually navigates there. The public
// search/booking flow (the highest-traffic path) loads first; portals
// nobody has logged into yet never cost anything.
const HomePage = lazy(() =>
  import('@/features/public/pages/HomePage').then((m) => ({ default: m.HomePage })),
);
const SearchResultsPage = lazy(() =>
  import('@/features/trip/pages/SearchResultsPage').then((m) => ({ default: m.SearchResultsPage })),
);
const TripDetailsPage = lazy(() =>
  import('@/features/trip/pages/TripDetailsPage').then((m) => ({ default: m.TripDetailsPage })),
);
const PassengerDetailsPage = lazy(() =>
  import('@/features/booking/pages/PassengerDetailsPage').then((m) => ({
    default: m.PassengerDetailsPage,
  })),
);
const BookingReviewPage = lazy(() =>
  import('@/features/booking/pages/BookingReviewPage').then((m) => ({
    default: m.BookingReviewPage,
  })),
);
const PaymentPage = lazy(() =>
  import('@/features/payment/pages/PaymentPage').then((m) => ({ default: m.PaymentPage })),
);
const BookingConfirmationPage = lazy(() =>
  import('@/features/booking/pages/BookingConfirmationPage').then((m) => ({
    default: m.BookingConfirmationPage,
  })),
);
const RoutesPage = lazy(() =>
  import('@/features/trip/pages/RoutesPage').then((m) => ({ default: m.RoutesPage })),
);
// QuickPay - roadside passenger payments (public page + driver + admin finance views).
const QuickPayPage = lazy(() =>
  import('@/features/quickpay/pages/QuickPayPage').then((m) => ({ default: m.QuickPayPage })),
);
const DriverQuickPaymentsPage = lazy(() =>
  import('@/features/quickpay/pages/DriverQuickPaymentsPage').then((m) => ({
    default: m.DriverQuickPaymentsPage,
  })),
);
const AdminQuickPaymentsPage = lazy(() =>
  import('@/features/quickpay/pages/AdminQuickPaymentsPage').then((m) => ({
    default: m.AdminQuickPaymentsPage,
  })),
);

// Accommodation booking flow (Phase 3c) - browse a property, hold rooms, reserve a stay,
// optionally linked to a ride under one TL-YYYYMMDD-NNNNNN reference (§15).
const StaySearchPage = lazy(() =>
  import('@/features/accommodation/pages/StaySearchPage').then((m) => ({
    default: m.StaySearchPage,
  })),
);
const PropertyDetailPage = lazy(() =>
  import('@/features/accommodation/pages/PropertyDetailPage').then((m) => ({
    default: m.PropertyDetailPage,
  })),
);
const StayReviewPage = lazy(() =>
  import('@/features/accommodation/pages/StayReviewPage').then((m) => ({
    default: m.StayReviewPage,
  })),
);
const StayPaymentPage = lazy(() =>
  import('@/features/accommodation/pages/StayPaymentPage').then((m) => ({
    default: m.StayPaymentPage,
  })),
);
const StayConfirmationPage = lazy(() =>
  import('@/features/accommodation/pages/StayConfirmationPage').then((m) => ({
    default: m.StayConfirmationPage,
  })),
);
const TripItineraryPage = lazy(() =>
  import('@/features/accommodation/pages/TripItineraryPage').then((m) => ({
    default: m.TripItineraryPage,
  })),
);
const RentalBookingPage = lazy(() =>
  import('@/features/rental/pages/RentalBookingPage').then((m) => ({
    default: m.RentalBookingPage,
  })),
);
const MyRentalsPage = lazy(() =>
  import('@/features/rental/pages/MyRentalsPage').then((m) => ({ default: m.MyRentalsPage })),
);

// Property portal (Phase 4e) - a property staff member's front desk / folio / housekeeping.
// Authorized by property-staff membership server-side, so only RequireAuth here.
const PropertyPickerPage = lazy(() =>
  import('@/features/property/pages/PropertyPickerPage').then((m) => ({
    default: m.PropertyPickerPage,
  })),
);
const PropertyFrontDeskPage = lazy(() =>
  import('@/features/property/pages/PropertyFrontDeskPage').then((m) => ({
    default: m.PropertyFrontDeskPage,
  })),
);
const PropertyRoomsPage = lazy(() =>
  import('@/features/property/pages/PropertyRoomsPage').then((m) => ({
    default: m.PropertyRoomsPage,
  })),
);
const PropertyPhotosPage = lazy(() =>
  import('@/features/property/pages/PropertyPhotosPage').then((m) => ({
    default: m.PropertyPhotosPage,
  })),
);
const PropertyActivityPage = lazy(() =>
  import('@/features/property/pages/PropertyActivityPage').then((m) => ({
    default: m.PropertyActivityPage,
  })),
);
const PropertyDashboardPage = lazy(() =>
  import('@/features/property/pages/PropertyDashboardPage').then((m) => ({
    default: m.PropertyDashboardPage,
  })),
);
const PropertyCalendarPage = lazy(() =>
  import('@/features/property/pages/PropertyCalendarPage').then((m) => ({
    default: m.PropertyCalendarPage,
  })),
);
const PropertySettingsPage = lazy(() =>
  import('@/features/property/pages/PropertySettingsPage').then((m) => ({
    default: m.PropertySettingsPage,
  })),
);
const PropertyReportsPage = lazy(() =>
  import('@/features/property/pages/PropertyReportsPage').then((m) => ({
    default: m.PropertyReportsPage,
  })),
);
const PropertyGuestsPage = lazy(() =>
  import('@/features/property/pages/PropertyGuestsPage').then((m) => ({
    default: m.PropertyGuestsPage,
  })),
);
const PropertyFolioPage = lazy(() =>
  import('@/features/property/pages/PropertyFolioPage').then((m) => ({
    default: m.PropertyFolioPage,
  })),
);
const PropertyHousekeepingPage = lazy(() =>
  import('@/features/property/pages/PropertyHousekeepingPage').then((m) => ({
    default: m.PropertyHousekeepingPage,
  })),
);
const PropertyInventoryPage = lazy(() =>
  import('@/features/property/pages/PropertyInventoryPage').then((m) => ({
    default: m.PropertyInventoryPage,
  })),
);
const PropertyProcurementPage = lazy(() =>
  import('@/features/property/pages/PropertyProcurementPage').then((m) => ({
    default: m.PropertyProcurementPage,
  })),
);
const PropertyMaintenancePage = lazy(() =>
  import('@/features/property/pages/PropertyMaintenancePage').then((m) => ({
    default: m.PropertyMaintenancePage,
  })),
);
const PropertyFinancePage = lazy(() =>
  import('@/features/property/pages/PropertyFinancePage').then((m) => ({
    default: m.PropertyFinancePage,
  })),
);
const PropertyHrPage = lazy(() =>
  import('@/features/property/pages/PropertyHrPage').then((m) => ({ default: m.PropertyHrPage })),
);
const PropertyPayrollPage = lazy(() =>
  import('@/features/property/pages/PropertyPayrollPage').then((m) => ({
    default: m.PropertyPayrollPage,
  })),
);
const PropertyIntegrationsPage = lazy(() =>
  import('@/features/property/pages/PropertyIntegrationsPage').then((m) => ({
    default: m.PropertyIntegrationsPage,
  })),
);

const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/features/auth/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const VerifyOtpPage = lazy(() =>
  import('@/features/auth/pages/VerifyOtpPage').then((m) => ({ default: m.VerifyOtpPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const ChangePasswordRequiredPage = lazy(() =>
  import('@/features/auth/pages/ChangePasswordRequiredPage').then((m) => ({
    default: m.ChangePasswordRequiredPage,
  })),
);

const ClientDashboardPage = lazy(() =>
  import('@/features/client/pages/ClientDashboardPage').then((m) => ({
    default: m.ClientDashboardPage,
  })),
);
const MyTripsPage = lazy(() =>
  import('@/features/client/pages/MyTripsPage').then((m) => ({ default: m.MyTripsPage })),
);
const ClientRatingsPage = lazy(() =>
  import('@/features/ridesharing/pages/ClientRatingsPage').then((m) => ({
    default: m.ClientRatingsPage,
  })),
);
const DriverRatingsPage = lazy(() =>
  import('@/features/ridesharing/pages/DriverRatingsPage').then((m) => ({
    default: m.DriverRatingsPage,
  })),
);
const AdminRatingsPage = lazy(() =>
  import('@/features/adminRideSharing/pages/AdminRatingsPage').then((m) => ({
    default: m.AdminRatingsPage,
  })),
);
const MyApplicationsPage = lazy(() =>
  import('@/features/client/pages/MyApplicationsPage').then((m) => ({
    default: m.MyApplicationsPage,
  })),
);
const BookingDetailsPage = lazy(() =>
  import('@/features/client/pages/BookingDetailsPage').then((m) => ({
    default: m.BookingDetailsPage,
  })),
);
const ClientComingSoonPage = lazy(() =>
  import('@/features/client/pages/ClientComingSoonPage').then((m) => ({
    default: m.ClientComingSoonPage,
  })),
);
const WalletPage = lazy(() =>
  import('@/features/wallet/pages/WalletPage').then((m) => ({ default: m.WalletPage })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notification/pages/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  })),
);

const PartnerDashboardPage = lazy(() =>
  import('@/features/partner/pages/PartnerDashboardPage').then((m) => ({
    default: m.PartnerDashboardPage,
  })),
);
const PartnerVehiclesPage = lazy(() =>
  import('@/features/partner/pages/PartnerVehiclesPage').then((m) => ({
    default: m.PartnerVehiclesPage,
  })),
);
const PartnerVehicleDetailPage = lazy(() =>
  import('@/features/partner/pages/PartnerVehicleDetailPage').then((m) => ({
    default: m.PartnerVehicleDetailPage,
  })),
);
const PartnerDriversPage = lazy(() =>
  import('@/features/partner/pages/PartnerDriversPage').then((m) => ({
    default: m.PartnerDriversPage,
  })),
);
const PartnerDriverDetailPage = lazy(() =>
  import('@/features/partner/pages/PartnerDriverDetailPage').then((m) => ({
    default: m.PartnerDriverDetailPage,
  })),
);
const PartnerTripsPage = lazy(() =>
  import('@/features/partner/pages/PartnerTripsPage').then((m) => ({
    default: m.PartnerTripsPage,
  })),
);
const PartnerOfferRidePage = lazy(() =>
  import('@/features/partner/pages/PartnerOfferRidePage').then((m) => ({
    default: m.PartnerOfferRidePage,
  })),
);
const PartnerTripDetailPage = lazy(() =>
  import('@/features/partner/pages/PartnerTripDetailPage').then((m) => ({
    default: m.PartnerTripDetailPage,
  })),
);
const PartnerEarningsPage = lazy(() =>
  import('@/features/partner/pages/PartnerEarningsPage').then((m) => ({
    default: m.PartnerEarningsPage,
  })),
);
const PartnerPayoutsPage = lazy(() =>
  import('@/features/partner/pages/PartnerPayoutsPage').then((m) => ({
    default: m.PartnerPayoutsPage,
  })),
);
const PartnerPayoutDetailPage = lazy(() =>
  import('@/features/partner/pages/PartnerPayoutDetailPage').then((m) => ({
    default: m.PartnerPayoutDetailPage,
  })),
);
const PartnerReportsPage = lazy(() =>
  import('@/features/partnerReports/pages/PartnerReportsPage').then((m) => ({
    default: m.PartnerReportsPage,
  })),
);

const DriverDashboardPage = lazy(() =>
  import('@/features/driver/pages/DriverDashboardPage').then((m) => ({
    default: m.DriverDashboardPage,
  })),
);
const DriverAssignmentsPage = lazy(() =>
  import('@/features/driver/pages/DriverAssignmentsPage').then((m) => ({
    default: m.DriverAssignmentsPage,
  })),
);
const DriverVehicleStatusPage = lazy(() =>
  import('@/features/driver/pages/DriverVehicleStatusPage').then((m) => ({
    default: m.DriverVehicleStatusPage,
  })),
);
const DriverOfferCarpoolRidePage = lazy(() =>
  import('@/features/driver/pages/DriverOfferCarpoolRidePage').then((m) => ({
    default: m.DriverOfferCarpoolRidePage,
  })),
);
const DriverTripPage = lazy(() =>
  import('@/features/driver/pages/DriverTripPage').then((m) => ({ default: m.DriverTripPage })),
);
const DriverActivityPage = lazy(() =>
  import('@/features/driverReports/pages/DriverActivityPage').then((m) => ({
    default: m.DriverActivityPage,
  })),
);

const AgentDashboardPage = lazy(() =>
  import('@/features/agent/pages/AgentDashboardPage').then((m) => ({
    default: m.AgentDashboardPage,
  })),
);
const AgentTripSearchPage = lazy(() =>
  import('@/features/agent/pages/AgentTripSearchPage').then((m) => ({
    default: m.AgentTripSearchPage,
  })),
);
const AgentBookingPassengerPage = lazy(() =>
  import('@/features/agent/pages/AgentBookingPassengerPage').then((m) => ({
    default: m.AgentBookingPassengerPage,
  })),
);
const AgentBookingReviewPage = lazy(() =>
  import('@/features/agent/pages/AgentBookingReviewPage').then((m) => ({
    default: m.AgentBookingReviewPage,
  })),
);
const AgentPaymentPage = lazy(() =>
  import('@/features/agent/pages/AgentPaymentPage').then((m) => ({ default: m.AgentPaymentPage })),
);
const AgentTicketPage = lazy(() =>
  import('@/features/agent/pages/AgentTicketPage').then((m) => ({ default: m.AgentTicketPage })),
);
const AgentBookingsPage = lazy(() =>
  import('@/features/agent/pages/AgentBookingsPage').then((m) => ({
    default: m.AgentBookingsPage,
  })),
);
const AgentTransactionsPage = lazy(() =>
  import('@/features/agent/pages/AgentTransactionsPage').then((m) => ({
    default: m.AgentTransactionsPage,
  })),
);
const AgentReportsPage = lazy(() =>
  import('@/features/agent/pages/AgentReportsPage').then((m) => ({ default: m.AgentReportsPage })),
);

const AdminApplicationsPage = lazy(() =>
  import('@/features/adminApplications/pages/AdminApplicationsPage').then((m) => ({
    default: m.AdminApplicationsPage,
  })),
);
const AdminAgentsPage = lazy(() =>
  import('@/features/adminAgent/pages/AdminAgentsPage').then((m) => ({
    default: m.AdminAgentsPage,
  })),
);

const AdminCorporateAccountsPage = lazy(() =>
  import('@/features/adminCorporate/pages/AdminCorporateAccountsPage').then((m) => ({
    default: m.AdminCorporateAccountsPage,
  })),
);

const CorporateDashboardPage = lazy(() =>
  import('@/features/corporate/pages/CorporateDashboardPage').then((m) => ({
    default: m.CorporateDashboardPage,
  })),
);
const CorporateProfilePage = lazy(() =>
  import('@/features/corporate/pages/CorporateProfilePage').then((m) => ({
    default: m.CorporateProfilePage,
  })),
);
const CorporateDepartmentsPage = lazy(() =>
  import('@/features/corporate/pages/CorporateDepartmentsPage').then((m) => ({
    default: m.CorporateDepartmentsPage,
  })),
);
const CorporateCostCentresPage = lazy(() =>
  import('@/features/corporate/pages/CorporateCostCentresPage').then((m) => ({
    default: m.CorporateCostCentresPage,
  })),
);
const CorporateEmployeesPage = lazy(() =>
  import('@/features/corporate/pages/CorporateEmployeesPage').then((m) => ({
    default: m.CorporateEmployeesPage,
  })),
);
const CorporateBookingsPage = lazy(() =>
  import('@/features/corporate/pages/CorporateBookingsPage').then((m) => ({
    default: m.CorporateBookingsPage,
  })),
);
const CorporateInvoicesPage = lazy(() =>
  import('@/features/corporate/pages/CorporateInvoicesPage').then((m) => ({
    default: m.CorporateInvoicesPage,
  })),
);
const CorporateTravelReportPage = lazy(() =>
  import('@/features/corporateReports/pages/CorporateTravelReportPage').then((m) => ({
    default: m.CorporateTravelReportPage,
  })),
);

const VerificationPartnersPage = lazy(() =>
  import('@/features/verification/pages/VerificationPartnersPage').then((m) => ({
    default: m.VerificationPartnersPage,
  })),
);
const VerificationDriversPage = lazy(() =>
  import('@/features/verification/pages/VerificationDriversPage').then((m) => ({
    default: m.VerificationDriversPage,
  })),
);
const VerificationVehiclesPage = lazy(() =>
  import('@/features/verification/pages/VerificationVehiclesPage').then((m) => ({
    default: m.VerificationVehiclesPage,
  })),
);
const VerificationDocumentsPage = lazy(() =>
  import('@/features/verification/pages/VerificationDocumentsPage').then((m) => ({
    default: m.VerificationDocumentsPage,
  })),
);
const AdminRuraVerificationPage = lazy(() =>
  import('@/features/adminRideSharing/pages/AdminRuraVerificationPage').then((m) => ({
    default: m.AdminRuraVerificationPage,
  })),
);
const AdminCarpoolDriverVerificationPage = lazy(() =>
  import('@/features/adminRideSharing/pages/AdminCarpoolDriverVerificationPage').then((m) => ({
    default: m.AdminCarpoolDriverVerificationPage,
  })),
);
const AdminPartnerDetailPage = lazy(() =>
  import('@/features/adminPartner/pages/AdminPartnerDetailPage').then((m) => ({
    default: m.AdminPartnerDetailPage,
  })),
);
const FleetOperationsDashboardPage = lazy(() =>
  import('@/features/fleet/pages/FleetOperationsDashboardPage').then((m) => ({
    default: m.FleetOperationsDashboardPage,
  })),
);

const AdminTripsPage = lazy(() =>
  import('@/features/adminTrip/pages/AdminTripsPage').then((m) => ({ default: m.AdminTripsPage })),
);
const AdminReportsPage = lazy(() =>
  import('@/features/adminReports/pages/AdminReportsPage').then((m) => ({
    default: m.AdminReportsPage,
  })),
);
const AdminTripDetailPage = lazy(() =>
  import('@/features/adminTrip/pages/AdminTripDetailPage').then((m) => ({
    default: m.AdminTripDetailPage,
  })),
);
const AdminSchedulePage = lazy(() =>
  import('@/features/adminTrip/pages/AdminSchedulePage').then((m) => ({
    default: m.AdminSchedulePage,
  })),
);
const AdminPaymentsFollowUpPage = lazy(() =>
  import('@/features/adminBookingOps/pages/AdminPaymentsFollowUpPage').then((m) => ({
    default: m.AdminPaymentsFollowUpPage,
  })),
);
const AdminRoutesPage = lazy(() =>
  import('@/features/adminRoute/pages/AdminRoutesPage').then((m) => ({
    default: m.AdminRoutesPage,
  })),
);
const AdminLocationsPage = lazy(() =>
  import('@/features/adminLocation/pages/AdminLocationsPage').then((m) => ({
    default: m.AdminLocationsPage,
  })),
);
const AdminRentalsPage = lazy(() =>
  import('@/features/adminRental/pages/AdminRentalsPage').then((m) => ({
    default: m.AdminRentalsPage,
  })),
);
const AdminPickupPointsPage = lazy(() =>
  import('@/features/adminLocation/pages/AdminPickupPointsPage').then((m) => ({
    default: m.AdminPickupPointsPage,
  })),
);
const AdminHospitalityDashboardPage = lazy(() =>
  import('@/features/adminProperty/pages/AdminHospitalityDashboardPage').then((m) => ({
    default: m.AdminHospitalityDashboardPage,
  })),
);
const AdminCommissionPage = lazy(() =>
  import('@/features/adminProperty/pages/AdminCommissionPage').then((m) => ({
    default: m.AdminCommissionPage,
  })),
);
const AdminSubscriptionPage = lazy(() =>
  import('@/features/adminProperty/pages/AdminSubscriptionPage').then((m) => ({
    default: m.AdminSubscriptionPage,
  })),
);
const AdminSettlementPage = lazy(() =>
  import('@/features/adminProperty/pages/AdminSettlementPage').then((m) => ({
    default: m.AdminSettlementPage,
  })),
);
const AdminPropertiesPage = lazy(() =>
  import('@/features/adminProperty/pages/AdminPropertiesPage').then((m) => ({
    default: m.AdminPropertiesPage,
  })),
);
const AdminPropertyDetailPage = lazy(() =>
  import('@/features/adminProperty/pages/AdminPropertyDetailPage').then((m) => ({
    default: m.AdminPropertyDetailPage,
  })),
);

const AdminPromotionsPage = lazy(() =>
  import('@/features/adminPromotion/pages/AdminPromotionsPage').then((m) => ({
    default: m.AdminPromotionsPage,
  })),
);
const AdminPricingRulesPage = lazy(() =>
  import('@/features/adminPricing/pages/AdminPricingRulesPage').then((m) => ({
    default: m.AdminPricingRulesPage,
  })),
);
const AdminRideSharingSettingsPage = lazy(() =>
  import('@/features/adminRideSharing/pages/AdminRideSharingSettingsPage').then((m) => ({
    default: m.AdminRideSharingSettingsPage,
  })),
);
const AdminRideSharingPricingPage = lazy(() =>
  import('@/features/adminRideSharing/pages/AdminRideSharingPricingPage').then((m) => ({
    default: m.AdminRideSharingPricingPage,
  })),
);
const AdminNotificationTemplatesPage = lazy(() =>
  import('@/features/adminNotificationTemplate/pages/AdminNotificationTemplatesPage').then((m) => ({
    default: m.AdminNotificationTemplatesPage,
  })),
);
const AdminSendNotificationPage = lazy(() =>
  import('@/features/adminNotificationTemplate/pages/AdminSendNotificationPage').then((m) => ({
    default: m.AdminSendNotificationPage,
  })),
);
const AdminIncidentsPage = lazy(() =>
  import('@/features/adminIncident/pages/AdminIncidentsPage').then((m) => ({
    default: m.AdminIncidentsPage,
  })),
);

const AdminWalletsPage = lazy(() =>
  import('@/features/adminWallet/pages/AdminWalletsPage').then((m) => ({
    default: m.AdminWalletsPage,
  })),
);
const AdminWalletDetailPage = lazy(() =>
  import('@/features/adminWallet/pages/AdminWalletDetailPage').then((m) => ({
    default: m.AdminWalletDetailPage,
  })),
);
const AdminRefundsPage = lazy(() =>
  import('@/features/adminRefund/pages/AdminRefundsPage').then((m) => ({
    default: m.AdminRefundsPage,
  })),
);
const AdminPayoutsPage = lazy(() =>
  import('@/features/adminPayout/pages/AdminPayoutsPage').then((m) => ({
    default: m.AdminPayoutsPage,
  })),
);
const AdminCommissionsPage = lazy(() =>
  import('@/features/adminCommission/pages/AdminCommissionsPage').then((m) => ({
    default: m.AdminCommissionsPage,
  })),
);
const AdminPaymentsPage = lazy(() =>
  import('@/features/adminPayment/pages/AdminPaymentsPage').then((m) => ({
    default: m.AdminPaymentsPage,
  })),
);

const AdminDashboardPage = lazy(() =>
  import('@/features/admin/pages/AdminDashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('@/features/adminUser/pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);

const UnauthorizedPage = lazy(() =>
  import('@/components/feedback/StatusPages').then((m) => ({ default: m.UnauthorizedPage })),
);
const NotFoundPage = lazy(() =>
  import('@/components/feedback/StatusPages').then((m) => ({ default: m.NotFoundPage })),
);

export function AppRouter() {
  // RequireAuth is the only other place this gets called, so a session
  // stored from a previous visit (refresh token in sessionStorage) never got
  // rehydrated into authStore until the user happened to land on a
  // RequireAuth-protected route - on a hard reload landing on a public page
  // (home, search, trip details, ...) the header would show "Log in /
  // Register" for an actually-logged-in user, and any auth-aware link on
  // that page (e.g. PublicLayout's "Become a partner") would treat them as
  // logged out. Calling it here, once, unconditionally, fixes that for
  // every route - it's already a no-op when there's no stored token (see
  // its `enabled` check).
  useCurrentUser();

  return (
    <Suspense fallback={<AppLoadingState />}>
      <Routes>
        {/* ---------------- Public: search & browse ---------------- */}
        <Route path={ROUTES.home} element={<HomePage />} />
        <Route path={ROUTES.searchResults} element={<SearchResultsPage />} />
        <Route path="/trips/:tripId" element={<TripDetailsPage />} />
        <Route path={ROUTES.routesBrowse} element={<RoutesPage />} />
        {/* QuickPay: no login, no booking - a roadside passenger pays a TripLink driver. */}
        <Route path={ROUTES.quickPay} element={<QuickPayPage />} />

        {/*
          Private rental - the form itself is public (it's a real nav
          destination), but getting a quote or booking both require login
          (RentalController lives under /client/rentals, not /public/*) -
          RentalBookingPage checks auth itself and redirects to /login
          rather than this route being wrapped in RequireAuth outright.
        */}
        <Route path={ROUTES.privateRental} element={<RentalBookingPage />} />

        {/* ---------------- Booking flow (requires login) ---------------- */}
        <Route
          path="/booking/:tripId/passengers"
          element={
            <RequireAuth>
              <PassengerDetailsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/booking/:tripId/review"
          element={
            <RequireAuth>
              <BookingReviewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/booking/:bookingId/payment"
          element={
            <RequireAuth>
              <PaymentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/booking/:bookingId/confirmation"
          element={
            <RequireAuth>
              <BookingConfirmationPage />
            </RequireAuth>
          }
        />

        {/* ---------------- Accommodation booking flow (Phase 3c, requires login) ---------------- */}
        <Route
          path={ROUTES.staySearch}
          element={
            <RequireAuth>
              <StaySearchPage />
            </RequireAuth>
          }
        />
        <Route
          path="/stay/properties/:propertyId"
          element={
            <RequireAuth>
              <PropertyDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.stayReview}
          element={
            <RequireAuth>
              <StayReviewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/stay/reservations/:reservationId/pay"
          element={
            <RequireAuth>
              <StayPaymentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/stay/reservations/:reservationId"
          element={
            <RequireAuth>
              <StayConfirmationPage />
            </RequireAuth>
          }
        />
        <Route
          path="/account/itinerary/:groupReference"
          element={
            <RequireAuth>
              <TripItineraryPage />
            </RequireAuth>
          }
        />

        {/* ---------------- Property portal / MIS (Phase 4e, property-staff membership) ---------------- */}
        <Route
          path={ROUTES.propertyPicker}
          element={
            <RequireAuth>
              <PropertyPickerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/dashboard"
          element={
            <RequireAuth>
              <PropertyDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/front-desk"
          element={
            <RequireAuth>
              <PropertyFrontDeskPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/calendar"
          element={
            <RequireAuth>
              <PropertyCalendarPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/guests"
          element={
            <RequireAuth>
              <PropertyGuestsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/rooms"
          element={
            <RequireAuth>
              <PropertyRoomsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/photos"
          element={
            <RequireAuth>
              <PropertyPhotosPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/activity"
          element={
            <RequireAuth>
              <PropertyActivityPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/front-desk/:reservationId/folio"
          element={
            <RequireAuth>
              <PropertyFolioPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/housekeeping"
          element={
            <RequireAuth>
              <PropertyHousekeepingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/inventory"
          element={
            <RequireAuth>
              <PropertyInventoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/procurement"
          element={
            <RequireAuth>
              <PropertyProcurementPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/maintenance"
          element={
            <RequireAuth>
              <PropertyMaintenancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/finance"
          element={
            <RequireAuth>
              <PropertyFinancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/hr"
          element={
            <RequireAuth>
              <PropertyHrPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/payroll"
          element={
            <RequireAuth>
              <PropertyPayrollPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/reports"
          element={
            <RequireAuth>
              <PropertyReportsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/integrations"
          element={
            <RequireAuth>
              <PropertyIntegrationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/property/:propertyId/settings"
          element={
            <RequireAuth>
              <PropertySettingsPage />
            </RequireAuth>
          }
        />

        {/* ---------------- Auth ---------------- */}
        <Route
          path={ROUTES.login}
          element={
            <RequireGuest>
              <LoginPage />
            </RequireGuest>
          }
        />
        <Route
          path={ROUTES.register}
          element={
            <RequireGuest>
              <RegisterPage />
            </RequireGuest>
          }
        />
        <Route path={ROUTES.verifyOtp} element={<VerifyOtpPage />} />
        <Route
          path={ROUTES.forgotPassword}
          element={
            <RequireGuest>
              <ForgotPasswordPage />
            </RequireGuest>
          }
        />
        <Route path={ROUTES.resetPassword} element={<ResetPasswordPage />} />
        <Route
          path={ROUTES.changePasswordRequired}
          element={
            <RequireAuth>
              <ChangePasswordRequiredPage />
            </RequireAuth>
          }
        />

        {/* ---------------- Client account (requires login) ---------------- */}
        <Route
          path={ROUTES.clientDashboard}
          element={
            <RequireAuth>
              <ClientDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.myTrips}
          element={
            <RequireAuth>
              <MyTripsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.myRatings}
          element={
            <RequireAuth>
              <ClientRatingsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.driverRatings}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverRatingsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminRatings}
          element={
            <RequireAuth>
              <RequirePermission permission="DRIVER_VIEW">
                <AdminRatingsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path="/account/trips/:bookingId"
          element={
            <RequireAuth>
              <BookingDetailsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.myRentals}
          element={
            <RequireAuth>
              <MyRentalsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.wallet}
          element={
            <RequireAuth>
              <WalletPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.notifications}
          element={
            <RequireAuth>
              <NotificationsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.myApplications}
          element={
            <RequireAuth>
              <MyApplicationsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.savedPassengers}
          element={
            <RequireAuth>
              <ClientComingSoonPage title="Saved passengers" />
            </RequireAuth>
          }
        />

        {/*
          ---------------- Partner portal ----------------
          No role check on the dashboard route itself, unlike its sub-pages
          below: PartnerDashboardPage IS the "become a partner" application
          form for a client who doesn't have the PARTNER role yet (see
          PartnerService.approve() on the backend - the role activates only
          once staff approve the application, not at register()/submit()).
          Gating this route on PARTNER would make it permanently unreachable
          for the exact person who needs it most: a first-time applicant.
        */}
        <Route
          path={ROUTES.partnerDashboard}
          element={
            <RequireAuth>
              <PartnerDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.partnerVehicles}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerVehiclesPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/partner/vehicles/:vehicleId"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerVehicleDetailPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.partnerDrivers}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerDriversPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/partner/drivers/:driverId"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerDriverDetailPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.partnerTrips}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerTripsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.partnerOfferRide}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerOfferRidePage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/partner/trips/:tripId"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerTripDetailPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.partnerEarnings}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerEarningsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.partnerPayouts}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerPayoutsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/partner/payouts/:payoutId"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerPayoutDetailPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.partnerReports}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.PARTNER]}>
                <PartnerReportsPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/* ---------------- Driver portal ---------------- */}
        <Route
          path={ROUTES.driverDashboard}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverDashboardPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.driverAssignments}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverAssignmentsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.driverVehicle}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverVehicleStatusPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.driverOfferCarpool}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverOfferCarpoolRidePage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.driverActivity}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverActivityPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.driverQuickPayments}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverQuickPaymentsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/driver/trips/:tripId"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.DRIVER]}>
                <DriverTripPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/*
          ---------------- Agent portal ----------------
          No role check on the dashboard route itself, unlike its sub-pages
          below: AgentDashboardPage IS the "become an agent" application form
          for a client who doesn't have the AGENT role yet (see
          AgentService.approve() on the backend - the role activates only
          once staff approve the application, not at register()/submit()).
          Gating this route on AGENT would make it permanently unreachable
          for the exact person who needs it most: a first-time applicant.
          Same pattern as the Partner portal block above.
        */}
        <Route
          path={ROUTES.agentDashboard}
          element={
            <RequireAuth>
              <AgentDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.agentBookTrip}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentTripSearchPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/agent/book/:tripId/passenger"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentBookingPassengerPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/agent/book/:tripId/review"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentBookingReviewPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/agent/book/:bookingId/payment"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentPaymentPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/agent/book/:bookingId/ticket"
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentTicketPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.agentBookings}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentBookingsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.agentTransactions}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentTransactionsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.agentReports}
          element={
            <RequireAuth>
              <RequireRole roles={[ROLES.AGENT]}>
                <AgentReportsPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/*
          ---------------- Corporate portal (Section 30) ----------------
          No role check on these routes: most of CorporateController still
          authorizes purely by "is authenticated AND owns a CorporateAccount
          row" (checked server-side per request), not by a role - only
          employee/department/cost-centre management and booking approval
          require the CORPORATE_ADMIN authority (@PreAuthorize server-side).
          CorporateService.approveAccount() grants CORPORATE_ADMIN once
          staff approve the account (register() itself does not) - but the
          dashboard route still can't be role-gated here, the same reason
          Partner's can't: CorporateDashboardPage IS the registration form
          for an account that doesn't exist/isn't approved yet, so gating it
          on the role would make it unreachable for a first-time applicant.
        */}
        <Route
          path={ROUTES.corporateDashboard}
          element={
            <RequireAuth>
              <CorporateDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.corporateProfile}
          element={
            <RequireAuth>
              <CorporateProfilePage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.corporateDepartments}
          element={
            <RequireAuth>
              <CorporateDepartmentsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.corporateCostCentres}
          element={
            <RequireAuth>
              <CorporateCostCentresPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.corporateEmployees}
          element={
            <RequireAuth>
              <CorporateEmployeesPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.corporateBookings}
          element={
            <RequireAuth>
              <CorporateBookingsPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.corporateInvoices}
          element={
            <RequireAuth>
              <CorporateInvoicesPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.corporateReports}
          element={
            <RequireAuth>
              <CorporateTravelReportPage />
            </RequireAuth>
          }
        />

        {/* ---------------- Admin: Verification Queue (Section 33) ---------------- */}
        <Route
          path={ROUTES.adminVerificationPartners}
          element={
            <RequireAuth>
              <RequirePermission permission="VERIFICATION_VIEW">
                <VerificationPartnersPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminVerificationDrivers}
          element={
            <RequireAuth>
              <RequirePermission permission="VERIFICATION_VIEW">
                <VerificationDriversPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminVerificationVehicles}
          element={
            <RequireAuth>
              <RequirePermission permission="VERIFICATION_VIEW">
                <VerificationVehiclesPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminVerificationDocuments}
          element={
            <RequireAuth>
              <RequirePermission permission="VERIFICATION_VIEW">
                <VerificationDocumentsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminVerificationRura}
          element={
            <RequireAuth>
              <RequirePermission permission="VERIFICATION_VIEW">
                <AdminRuraVerificationPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminVerificationCarpoolDrivers}
          element={
            <RequireAuth>
              <RequirePermission permission="VERIFICATION_VIEW">
                <AdminCarpoolDriverVerificationPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/partners/:partnerId"
          element={
            <RequireAuth>
              <RequirePermission permission="PARTNER_VIEW">
                <AdminPartnerDetailPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* ---------------- Admin: Fleet Operations (Partner/Fleet Management) ---------------- */}
        <Route
          path={ROUTES.adminFleet}
          element={
            <RequireAuth>
              <RequirePermission permission="FLEET_OPERATIONS_VIEW">
                <FleetOperationsDashboardPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* ---------------- Admin: Trips / Routes / Locations (Sections 34-35) ---------------- */}
        <Route
          path={ROUTES.adminTrips}
          element={
            <RequireAuth>
              <RequirePermission permission="TRIP_VIEW">
                <AdminTripsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        {/* ---------------- Admin Reporting Center (Enterprise Reporting & Analytics, Phase 1) ---------------- */}
        <Route
          path={ROUTES.adminReports}
          element={
            <RequireAuth>
              <RequirePermission
                anyOf={[
                  'REPORT_OPERATIONS_VIEW',
                  'REPORT_BOOKINGS_VIEW',
                  'REPORT_FINANCE_VIEW',
                  'REPORT_DRIVER_VIEW',
                  'REPORT_AGENT_COMMISSION_VIEW',
                ]}
              >
                <AdminReportsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trips/:tripId"
          element={
            <RequireAuth>
              <RequirePermission permission="TRIP_VIEW">
                <AdminTripDetailPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminSchedule}
          element={
            <RequireAuth>
              <RequirePermission permission="TRIP_VIEW">
                <AdminSchedulePage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminPaymentsFollowUp}
          element={
            <RequireAuth>
              <RequirePermission anyOf={['TRIP_VIEW', 'PAYMENT_VIEW', 'REFUND_VIEW']}>
                <AdminPaymentsFollowUpPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminRoutes}
          element={
            <RequireAuth>
              <RequirePermission permission="ROUTE_VIEW">
                <AdminRoutesPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminLocations}
          element={
            <RequireAuth>
              <RequirePermission permission="LOCATION_VIEW">
                <AdminLocationsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminPickupPoints}
          element={
            <RequireAuth>
              <RequirePermission permission="LOCATION_VIEW">
                <AdminPickupPointsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminRentals}
          element={
            <RequireAuth>
              <RequirePermission permission="RENTAL_VIEW">
                <AdminRentalsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminHospitalityDashboard}
          element={
            <RequireAuth>
              <RequirePermission permission="PROPERTY_VIEW">
                <AdminHospitalityDashboardPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminHospitalityCommission}
          element={
            <RequireAuth>
              <RequirePermission permission="PROPERTY_VIEW">
                <AdminCommissionPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminHospitalitySettlements}
          element={
            <RequireAuth>
              <RequirePermission permission="PROPERTY_VIEW">
                <AdminSettlementPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminHospitalitySubscriptions}
          element={
            <RequireAuth>
              <RequirePermission permission="PROPERTY_VIEW">
                <AdminSubscriptionPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminProperties}
          element={
            <RequireAuth>
              <RequirePermission permission="PROPERTY_VIEW">
                <AdminPropertiesPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/properties/:id"
          element={
            <RequireAuth>
              <RequirePermission permission="PROPERTY_VIEW">
                <AdminPropertyDetailPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* ---------------- Admin: Promotions / Pricing / Notification templates / Incidents (Sections 37, 38, 41.3) ---------------- */}
        <Route
          path={ROUTES.adminPromotions}
          element={
            <RequireAuth>
              <RequirePermission permission="PROMOTION_VIEW">
                <AdminPromotionsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminPricingRules}
          element={
            <RequireAuth>
              <RequirePermission permission="PRICING_VIEW">
                <AdminPricingRulesPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminRideSharingSettings}
          element={
            <RequireAuth>
              <RequirePermission permission="PRICING_VIEW">
                <AdminRideSharingSettingsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminRideSharingPricing}
          element={
            <RequireAuth>
              <RequirePermission permission="PRICING_VIEW">
                <AdminRideSharingPricingPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminNotificationTemplates}
          element={
            <RequireAuth>
              <RequirePermission permission="NOTIFICATION_VIEW">
                <AdminNotificationTemplatesPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminSendNotification}
          element={
            <RequireAuth>
              <RequirePermission permission="NOTIFICATION_MANAGE">
                <AdminSendNotificationPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminIncidents}
          element={
            <RequireAuth>
              <RequirePermission permission="INCIDENT_VIEW">
                <AdminIncidentsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* ---------------- Admin: Finance - Wallets / Refunds / Payouts / Commissions (Section 39) ---------------- */}
        <Route
          path={ROUTES.adminWallets}
          element={
            <RequireAuth>
              <RequirePermission permission="WALLET_VIEW">
                <AdminWalletsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/wallets/:walletId"
          element={
            <RequireAuth>
              <RequirePermission permission="WALLET_VIEW">
                <AdminWalletDetailPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminRefunds}
          element={
            <RequireAuth>
              <RequirePermission permission="REFUND_VIEW">
                <AdminRefundsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminPayouts}
          element={
            <RequireAuth>
              <RequirePermission permission="PAYOUT_VIEW">
                <AdminPayoutsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminCommissions}
          element={
            <RequireAuth>
              <RequirePermission permission="COMMISSION_VIEW">
                <AdminCommissionsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminPayments}
          element={
            <RequireAuth>
              <RequirePermission permission="PAYMENT_VIEW">
                <AdminPaymentsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminQuickPayments}
          element={
            <RequireAuth>
              <RequirePermission permission="QUICKPAY_VIEW">
                <AdminQuickPaymentsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        {/* ---------------- Admin dashboard (Section 31) ---------------- */}
        <Route
          path={ROUTES.adminDashboard}
          element={
            <RequireAuth>
              <RequireRole roles={STAFF_ROLES}>
                <AdminDashboardPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/*
          User management - SUPER_ADMIN only, matching AdminUserController's
          own @PreAuthorize exactly (not the broader STAFF_ROLES the rest of
          /admin uses): this is where staff accounts are created and any
          account's roles can be changed, so it's scoped tighter than the
          admin dashboard hub above it.
        */}
        <Route
          path={ROUTES.adminUsers}
          element={
            <RequireAuth>
              <RequirePermission permission="STAFF_VIEW">
                <AdminUsersPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminApplications}
          element={
            <RequireAuth>
              <RequirePermission anyOf={['PARTNER_VIEW', 'CORPORATE_ACCOUNT_MANAGE', 'AGENT_VIEW']}>
                <AdminApplicationsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminAgents}
          element={
            <RequireAuth>
              <RequirePermission permission="AGENT_VIEW">
                <AdminAgentsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.adminCorporateAccounts}
          element={
            <RequireAuth>
              <RequirePermission permission="CORPORATE_ACCOUNT_MANAGE">
                <AdminCorporateAccountsPage />
              </RequirePermission>
            </RequireAuth>
          }
        />

        <Route path={ROUTES.unauthorized} element={<UnauthorizedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
